import { useMutation } from '@tanstack/react-query'
import { Alert, App as AntdApp, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

import { organizationsAccessImpersonateCreate } from 'src/api/main'
import { useShowApiErrors } from 'src/utils'
import { startImpersonation } from 'src/utils/impersonation'

import type { WorkspaceUser } from '../types'
import { composeDisplayName } from '../utils'

type Props = {
  open: boolean
  user: WorkspaceUser | null
  onClose: () => void
}

export const UserImpersonateModal = ({ open, user, onClose }: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()

  const name = user
    ? composeDisplayName(
        user.user.first_name,
        user.user.last_name,
        user.user.email
      )
    : ''

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No member selected')
      const { data } = await organizationsAccessImpersonateCreate({
        path: { id: user.id },
        throwOnError: true
      })

      return data
    },
    onSuccess: (result) => {
      message.success(t('users.lifecycle.impersonate.success'))
      // Swaps tokens + full reload, so this component unmounts mid-call.
      startImpersonation({
        access: result.access,
        refresh: result.refresh,
        name
      })
    },
    onError: (e) => showApiErrors(e)
  })

  return (
    <Modal
      open={open}
      centered
      title={t('users.lifecycle.impersonate.title')}
      okText={t('users.lifecycle.impersonate.submit')}
      cancelText={t('users.modals.cancel')}
      okButtonProps={{ loading: isPending }}
      cancelButtonProps={{ disabled: isPending }}
      onOk={() => void mutateAsync()}
      onCancel={() => {
        if (!isPending) onClose()
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, color: 'var(--ant-color-text-secondary)' }}>
        {t('users.lifecycle.impersonate.description', { name })}
      </p>
      <Alert
        type="warning"
        showIcon
        message={t('users.lifecycle.impersonate.warning')}
      />
    </Modal>
  )
}
